"use client";
import { useEffect, useRef, useState } from "react";
import { store } from "../lib/store.js";

//styles
import Logo from "../styles/img/header.svg";

//Redux
import { scanToken } from "../lib/slices/paladin";

//Libs
import { datadogLogs } from "@datadog/browser-logs";
import { MdCancel } from "react-icons/md";
import toast, { Toaster } from "react-hot-toast";

//Hooks
import RoundLoader from "../Hooks/RoudLoader.js";

//Utils
import { isVerifiedMobileDevice } from "../Utils/deviceValidation";

//Components
import InvitationFail from "../Components/InvitationFail";
import Error from "../Components/Results/Error.js";
import Unknown from "../Components/Results/Unknown.js";
import Success from "../Components/Results/Success.js";
import Header from "../Components/Common/header.js";
import {
    postCreateIncome,
    postExchangeIncome,
    postLinkIncome,
} from "../lib/slices/income.js";

//constants
import { ALERTS_STYLES, ALERTS_POSITION_SELFIE } from "../constants/alerts.js";

const Income = () => {
    const [loading, setLoading] = useState(false);
    const { dispatch } = store;
    const urlSearchParams = new URLSearchParams(window.location.search);
    const token = urlSearchParams.get("token");
    const [tenantColor, setTenantColor] = useState(localStorage.getItem(`tenantColor_${token}`) || "#6A2ABD");
    const [tenantImage, setTenantImage] = useState(Logo);
    const [tenantName, setTenantName] = useState("");
    const [responseScan, setResponseScan] = useState(null);
    const [loadingResult, setLoadingResult] = useState(false);
    const [result, setResult] = useState(null);
    const [dataLink, setDataLink] = useState(null);
    const handlerRef = useRef(null);
    const [typeLink, setTypeLink] = useState(null);
    const [redirect, setRedirect] = useState(false);

    const queryData = async () => {
        setLoading(true);
        var data = { token };

        try {
            const response = await dispatch(scanToken({ data }));
            const responseIncome = await dispatch(postCreateIncome({ data }));
            setResponseScan(response?.payload?.ScanToken);
            const resolvedColor = response?.payload?.ScanToken?.custom_color || "#6A2ABD";
            setTenantColor(resolvedColor);
            localStorage.setItem(`tenantColor_${token}`, resolvedColor);
            setTenantImage(
                response?.payload?.ScanToken?.custom_image &&
                `data:image/jpeg;base64,${response?.payload?.ScanToken?.custom_image}`,
            );
            setTenantName(
                response?.payload?.ScanToken?.account_name
                    ? response?.payload?.ScanToken?.account_name
                    : "",
            );

            datadogLogs.logger.log("Token validated (plaid)", {
                category: "token-validation",
                status: "info",
                tenantName: response?.payload?.ScanToken?.account_name,
                invitationId: response?.payload?.ScanToken?.invitation_id,
            });
            if (responseIncome?.error) {
                datadogLogs.logger.log(
                    "Error validating token (plaid) - additional data",
                    {
                        category: "error-handling",
                        status: "error",
                        token: token,
                        errorMessage: responseIncome,
                    },
                );
                setResult(2);
            } else {
                datadogLogs.logger.log("Income created (plaid)", {
                    category: "account-linking",
                    status: "info",
                    tenantName: response?.payload?.ScanToken?.account_name,
                    invitationId: response?.payload?.ScanToken?.invitation_id,
                    responseIncome: responseIncome,
                });
            }
        } catch (error) {
            datadogLogs.logger.log(
                "Error validating token (plaid) - additional data",
                {
                    category: "error-handling",
                    status: "error",
                    token: token,
                    errorMessage: error?.message,
                },
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isVerifiedMobileDevice()) {
            queryData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const createLink = async (type) => {
        setTypeLink(type);
        setLoading(true);
        var data = { token, type };

        try {
            const response = await dispatch(postLinkIncome({ data }));

            if (!response?.error) {
                setDataLink(response?.payload?.status);
                datadogLogs.logger.log("Link created (plaid)", {
                    category: "account-linking",
                    status: "info",
                    tenantName: responseScan?.account_name,
                    invitationId: responseScan?.invitation_id,
                    response: response?.payload,
                    data: data,
                });
            } else {
                toast.error("Something went wrong. Please try again later.");
                datadogLogs.logger.log(
                    "Error creating link (plaid) - additional data",
                    {
                        category: "error-handling",
                        status: "error",
                        token: token,
                        errorMessage: response,
                        data: data,
                        tenantName: responseScan?.account_name,
                        invitationId: responseScan?.invitation_id,
                    },
                );
            }
        } catch (error) {
            toast.error("An unexpected error occurred. Please try again later.");
            datadogLogs.logger.log("Error creating link (plaid) - additional data", {
                category: "error-handling",
                status: "error",
                token: token,
                errorMessage: error,
                data: data,
                tenantName: responseScan?.account_name,
                invitationId: responseScan?.invitation_id,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(
        () => {
            if (!dataLink?.link_token) return;
            if (!window.Plaid) {
                datadogLogs.logger.log("Plaid script not loaded", {
                    category: "error-handling",
                    status: "error",
                    tenantName: responseScan?.account_name,
                    invitationId: responseScan?.invitation_id,
                });
                return;
            }

            handlerRef.current = window.Plaid.create({
                token: dataLink?.link_token,

                onSuccess: async (public_token, metadata) => {
                    // 2.a: send public_token to backend to exchange for access_token

                    // update your UI
                    datadogLogs.logger.log("Plaid success", {
                        category: "account-linking",
                        status: "info",
                        tenantName: responseScan?.account_name,
                        invitationId: responseScan?.invitationId,
                        public_token: public_token,
                        metadata: metadata,
                    });
                    setLoadingResult(true);
                    var data = {
                        token: token,
                        type: typeLink,
                        public_token: metadata.public_token,
                    };
                    try {
                        const response = await dispatch(postExchangeIncome({ data }));

                        if (!response?.error) {
                            datadogLogs.logger.log("Account linked (plaid)", {
                                category: "account-linking",
                                status: "info",
                                tenantName: responseScan?.account_name,
                                invitationId: responseScan?.invitation_id,
                                response: response?.payload?.status,
                            });
                            setRedirect(response?.payload?.status?.redirect || false);
                            if (response?.payload?.status?.success === true) {
                                setResult(0);
                            } else {
                                setResult(2);
                            }
                        } else {
                            setResult(2);
                            datadogLogs.logger.log(
                                "Error linking account (plaid) - additional data",
                                {
                                    category: "error-handling",
                                    status: "error",
                                    token: token,
                                    errorMessage: response?.error,
                                    tenantName: responseScan?.account_name,
                                    invitationId: responseScan?.invitation_id,
                                },
                            );
                        }
                    } catch (error) {
                        datadogLogs.logger.log(
                            "Error linking account (plaid) - additional data",
                            {
                                category: "error-handling",
                                status: "error",
                                token: token,
                                errorMessage: error?.message,
                                tenantName: responseScan?.account_name,
                                invitationId: responseScan?.invitation_id,
                            },
                        );
                    } finally {
                        setLoadingResult(false);
                    }
                },

                onExit: (err, metadata) => {
                    // user cancelled or an error occurred
                    datadogLogs.logger.log("Plaid exited", {
                        category: "account-linking",
                        status: "info",
                        err: err,
                        tenantName: responseScan?.account_name,
                        invitationId: responseScan?.invitation_id,
                        metadata: metadata,
                    });

                    // notify parent if inside iframe
                    try {
                        window.parent?.postMessage?.(
                            {
                                type: "plaid-exit",
                                error: err ? err.display_message || err.message : null,
                                metadata,
                            },
                            "*",
                        );
                    } catch (e) { }
                },

                onEvent: (eventName, metadata) => {
                    // optional: receive Link events for analytics/debugging
                    console.log("Plaid event", eventName, metadata);
                },
            });
            handlerRef.current.open();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dataLink],
    );

    return (
        <>
            <main className="w-full">
                {loadingResult ? (
                    <div className="flex flex-col gap-8 h-screen row-start-2 items-center justify-center w-full container mx-auto p-3 py-9">
                        <RoundLoader
                            loading={true}
                            color={tenantColor}
                            title="Analyzing"
                            text="Please don’t close the browser.
                            This will take a moment."
                        />
                    </div>
                ) : (
                    <>
                        {loading ? (
                            <div className="flex flex-col gap-8 h-screen row-start-2 items-center justify-center w-full container mx-auto p-3 py-9">
                                <RoundLoader loading={loading} color={tenantColor} />
                            </div>
                        ) : (
                            <>
                                {isVerifiedMobileDevice() ? (
                                    <>
                                        {responseScan?.invitation_id && (
                                            <>
                                                <section className="flex flex-col items-center justify-start w-full">
                                                    {result === 0 ? (
                                                        <Success
                                                            token={token}
                                                            responseScan={responseScan}
                                                            tenantName={tenantName}
                                                            tenantColor={tenantColor}
                                                            tenantImage={tenantImage}
                                                            redirect={redirect}
                                                        />
                                                    ) : result === 1 ? (
                                                        <Unknown
                                                            token={token}
                                                            responseScan={responseScan}
                                                            tenantName={tenantName}
                                                            tenantColor={tenantColor}
                                                            tenantImage={tenantImage}
                                                        />
                                                    ) : result === 2 ? (
                                                        <Error
                                                            token={token}
                                                            responseScan={responseScan}
                                                            tenantName={tenantName}
                                                            tenantColor={tenantColor}
                                                            tenantImage={tenantImage}
                                                            redirect={redirect}
                                                        />
                                                    ) : (
                                                        <>
                                                            <section className="w-full h-[85%] flex flex-col items-center justify-start container mx-auto p-3 py-9">
                                                                <Header
                                                                    tenantImage={tenantImage}
                                                                    tenantName={tenantName}
                                                                    tenantColor={tenantColor}
                                                                />
                                                                <h2 className="text-2xl text-center my-2 mb-4">
                                                                    Income Verification
                                                                </h2>

                                                                <p className="text-center">
                                                                    This process allows us to securely verify your
                                                                    income information to ensure accurate results.
                                                                    Your data will be used only for verification
                                                                    purposes.
                                                                </p>

                                                                <button
                                                                    onClick={() => createLink(0)}
                                                                    style={{ backgroundColor: tenantColor }}
                                                                    className="text-white px-9 py-4 rounded-full text-nowrap mt-16"
                                                                >
                                                                    {loading ? (
                                                                        <RoundLoader
                                                                            loading={true}
                                                                            color="white"
                                                                            size={24}
                                                                        />
                                                                    ) : (
                                                                        "Payroll"
                                                                    )}
                                                                </button>

                                                                <button
                                                                    onClick={() => createLink(1)}
                                                                    style={{ backgroundColor: tenantColor }}
                                                                    className="text-white px-9 py-4 rounded-full text-nowrap mt-8"
                                                                >
                                                                    {loading ? (
                                                                        <RoundLoader
                                                                            loading={true}
                                                                            color="white"
                                                                            size={24}
                                                                        />
                                                                    ) : (
                                                                        "Bank"
                                                                    )}
                                                                </button>
                                                            </section>
                                                        </>
                                                    )}
                                                </section>
                                            </>
                                        )}
                                        {(responseScan?.error || token === null) && (
                                            <InvitationFail />
                                        )}
                                    </>
                                ) : (
                                    <div className="flex justify-center items-center min-h-screen flex-col gap-3 container px-3 py-5 bg-[#F8F7F6] w-full">
                                        <MdCancel size={150} className="text-[#E43122]" />
                                        <h2 className="text-2xl text-[#E43122]">Error</h2>
                                        <p className="my-5 text-center text-sm">
                                            PALIDIN Income is not available for desktop computers.{" "}
                                            <br></br>
                                            Please open the authentication invitation on a mobile
                                            device.
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                <Toaster
                    position={ALERTS_POSITION_SELFIE}
                    reverseOrder={false}
                    toastOptions={ALERTS_STYLES}
                />
            </main>
        </>
    );
};
export default Income;
